import { Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DEFAULT_LANGUAGE, EMPTY_ARRAY_LENGTH, IFormDelegate, IFormOptions } from '@modeso/aumico-lib-core-fe';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { IAccountingBasisDTO, IAccountingBasisExtendedDTO } from '@modeso/types__aumico-lib-settings-be';
import { IAccountingBasis } from '@modeso/types__aumico-lib-settings-be/dist/models/interfaces/IAccountingBasis.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { Subject, filter } from 'rxjs';
import { IAccountingBasisFormData } from '../../interfaces/accounting-basis-form-data.interface';
import { getDebug } from './../../utils/debugUtil';

const filename =
    'projects/aumico-lib-settings-fe/src/lib/components/accounting-basis-form/accounting-basis-form.component.ts';
const debug = getDebug(filename);

@UntilDestroy()
@Component({
    // tslint:disable-next-line: component-selector
    selector: 'aumico-settings-accounting-basis-form',
    templateUrl: './accounting-basis-form.component.html',
    styleUrls: ['./accounting-basis-form.component.scss'],
})
export class AccountingBasisFormComponent implements OnInit, IFormDelegate {
    countries: INameId[] = [];
    countryISO31661Alpha2CodesFormControl = new FormControl(null);
    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: IAccountingBasisFormData,
        private i18nextService: I18NextService,
    ) {
        this.countries = data.countries;
        const accountBasis = this.data.accountingBasis;
        if (accountBasis != null) {
            this.setCountryISO31661Alpha2CodesFormControlValue(accountBasis);
        }
        this.handleItemUpdated();
    }
    @Input() languages: string[] = ['de-ch', 'en-us'];

    @ViewChild('createUpdateForm', { static: false })
    public createUpdateForm: ElementRef;

    protected subscriptions = [];

    options: IFormOptions;

    public savePressed = new Subject<IAccountingBasisDTO>();

    public nameAlreadyExistsError = false;

    public ngOnInit(): void {
        this.data.errorMessage
            .pipe(
                filter(
                    (accountingBasisResponse) =>
                        accountingBasisResponse != null && accountingBasisResponse.status === 409,
                ),
                untilDestroyed(this),
            )
            .subscribe(() => {
                this.nameAlreadyExistsError = true;
            });
        this.prepareFields();
    }

    private setCountryISO31661Alpha2CodesFormControlValue(accountBasis: IAccountingBasis): void {
        const countryISO31661Alpha2Codes = accountBasis.countryISO31661Alpha2Codes;
        if (countryISO31661Alpha2Codes == null || countryISO31661Alpha2Codes.length === EMPTY_ARRAY_LENGTH) {
            return;
        }

        const selectedCountries = this.countries.filter((country) => countryISO31661Alpha2Codes.includes(country.id));
        this.countryISO31661Alpha2CodesFormControl.setValue(selectedCountries);
    }

    private handleItemUpdated(): void {
        if (this.data.accountingBasis != null) {
            this.data.accountingBasis.text = this.data.accountingBasis.text;
            for (const lang of this.languages) {
                this.data.accountingBasis.text[lang] = this.data.accountingBasis.text[lang] || '';
            }
            debug('ngOnInit getAll result');
            debug(this.data);
            this.prepareFields(this.data.accountingBasis._id == null ? 'create' : 'edit');
        }
    }

    public onSubmit(result: IAccountingBasisExtendedDTO, formOptions?: IFormOptions): void {
        debug('result of the form');
        debug(result);
        const selectedCountries: INameId[] = [];

        const countryISO31661Alpha2CodesFormControlValue = this.countryISO31661Alpha2CodesFormControl.value;
        if (countryISO31661Alpha2CodesFormControlValue != null) {
            selectedCountries.push(...(countryISO31661Alpha2CodesFormControlValue as INameId[]));
        }

        // map IAccountingBasisExtendedDTO to IAccountingBasisDTO
        const accountingBasisToSave: IAccountingBasisDTO = {
            _id: result._id ? result._id : undefined,
            name: result.name,
            text: result.text,
            countryISO31661Alpha2Codes: selectedCountries.map((country) => country.id),
        };
        this.savePressed.next(accountingBasisToSave);
    }

    async prepareFields(action: 'create' | 'edit' = 'create') {
        this.options = {
            properties: [
                {
                    name: this.i18nextService.t('aumico_settings_accountingbasis_id') as string,
                    type: 'label',
                    property: '_id',
                    required: false,
                },
                {
                    name: this.i18nextService.t('aumico_settings_accountingbasis_version') as string,
                    type: 'label',
                    property: 'version',
                    required: false,
                },
                {
                    name: this.i18nextService.t('aumico_settings_accountingbasis_owner') as string,
                    type: 'label',
                    property: 'owner',
                    required: false,
                },
                {
                    name: this.i18nextService.t('aumico_settings_accountingbasis_name') as string,
                    type: action === 'create' ? 'text' : 'label',
                    property: 'name',
                    required: true,
                },
                {
                    name: this.i18nextService.t('aumico_settings_accountingbasis_text') as string,
                    type: 'translatable-text',
                    property: 'text',
                    required: true,
                    languages: [DEFAULT_LANGUAGE],
                },
            ],
            submitText: this.i18nextService.t('aumico_button_save') as string,
        };
    }
}
